import type { PlanDay } from '@painpal/contracts';
import { weekEnd, weekStart } from '@painpal/core';
import { useState } from 'react';
import { usePlan } from '../../api/queries';
import { Eyebrow } from '../../components/Eyebrow';
import { Headline } from '../../components/Headline';
import { ErrorPanel, LoadingBlock } from '../../components/StateViews';
import { formatWeekdayMonthDay } from '../../lib/format';
import { Presence } from '../../ui/Presence';
import { CalendarRow } from './CalendarRow';
import { CalendarWeekHeader } from './CalendarWeekHeader';
import { CopyLastWeek } from './CopyLastWeek';
import { DayEditor } from './DayEditor';
import { isoWeek, progressPercent, ridingDaysLine, weekPlanTotals, type WeekPlanTotals } from './planModel';
import styles from './calendar.module.css';

export interface CalendarPageProps { today: string; week: string }

function PageHeader({ today, week, totals }: { today: string; week: string; totals: WeekPlanTotals | undefined }) {
  return (
    <header className={styles.header}>
      <div className="page-heading">
        <Eyebrow>
          {formatWeekdayMonthDay(today)} · WEEK {isoWeek(week)}
          {totals !== undefined && <span className="sr-only-phone"> · {ridingDaysLine(totals)}</span>}
        </Eyebrow>
        <Headline first="PLAN THE WEEK," second="RIDE THE DAY." />
      </div>
      <p className={styles.hint}>CLICK A DAY TO EDIT ITS BLOCK<span className={styles.hintQuiet}>LOAD IS AN ESTIMATE UNTIL YOU RIDE</span></p>
    </header>
  );
}

function Progress({ totals }: { totals: WeekPlanTotals }) {
  return (
    <div className={styles.progress} aria-hidden="true">
      <span className={styles.progressFill} data-part="progress" style={{ width: `${progressPercent(totals)}%` }} />
    </div>
  );
}

function ColumnHeads() {
  return <div className={styles.heads} aria-hidden="true"><span>DAY</span><span>PLAN</span><span>NOTE</span><span>EST. LOAD</span><span>RIDDEN</span><span /></div>;
}

interface RowsProps { days: readonly PlanDay[]; today: string; open: string | null; onOpen: (date: string) => void; onClose: () => void }

// Each row item anchors its own editor, so the popover sits by the row and an outside click is outside the item.
function WeekRows({ days, today, open, onOpen, onClose }: RowsProps) {
  return (
    <ol className={styles.rows} aria-label="Days">
      {days.map((day) => (
        <li key={day.date} className={styles.rowItem} data-editor-anchor="">
          <CalendarRow day={day} today={today} expanded={open === day.date} onEdit={() => onOpen(day.date)} />
          <Presence show={open === day.date}><DayEditor day={day} onClose={onClose} /></Presence>
        </li>
      ))}
    </ol>
  );
}

export function CalendarPage({ today: browserToday, week }: CalendarPageProps) {
  const plan = usePlan(week, weekEnd(week));
  // The statuses were judged against the profile zone's today, so TODAY marks that day once the server has said.
  const today = plan.data?.today ?? browserToday;
  const [editing, setEditing] = useState<string | null>(null);
  const [editingWeek, setEditingWeek] = useState(week);
  // Paging closes the editor, so coming back to its week (back, forward, keyboard) doesn't reopen it.
  if (editingWeek !== week) {
    setEditingWeek(week);
    setEditing(null);
  }
  // While the next week loads the previous week's rows stay (faded), but an editor only opens on the shown week.
  const open = editing !== null && editing >= week && editing <= weekEnd(week) ? editing : null;
  const days = plan.data?.days;
  const totals = days === undefined ? undefined : weekPlanTotals(days);
  return (
    <div className="page">
      <PageHeader today={today} week={week} totals={totals} />
      <section className={styles.card} aria-label="Week" aria-busy={plan.isPlaceholderData} data-placeholder={plan.isPlaceholderData || undefined}>
        <CalendarWeekHeader week={week} thisWeek={weekStart(browserToday)} totals={totals} />
        {plan.isPending && <LoadingBlock label="LOADING THE WEEK" height={448} />}
        {plan.isError && days === undefined && <ErrorPanel error={plan.error} onRetry={() => void plan.refetch()} />}
        {days !== undefined && totals !== undefined && (
          <>
            {totals.blocks > 0 ? <><Progress totals={totals} /><ColumnHeads /></> : <CopyLastWeek key={week} week={week} disabled={plan.isPlaceholderData} />}
            <WeekRows days={days} today={today} open={open} onOpen={setEditing} onClose={() => setEditing(null)} />
          </>
        )}
      </section>
      {days?.some((day) => day.plan?.defaulted === true) === true && <p className={styles.footnote}>* NO DURATION SET · USES THE TYPE&apos;S DEFAULT</p>}
    </div>
  );
}
